import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { FormBlockContext } from '#layers/forms/types/form-block';
import { computed, toValue } from 'vue';

export interface UseFormFieldChangesReturn<T = unknown> {
	/** True while the field holds a value that differs from the one the form was loaded with. */
	hasChanges: ComputedRef<boolean>;
	/** Value the form was loaded with, or undefined when the field is unchanged. */
	originalValue: ComputedRef<T | undefined>;
}

/**
 * Tracks whether a single field of the surrounding form block has unsaved changes.
 *
 * Reads rstore's `$changedProps`, which drops a field's entry once its value matches the loaded
 * one again. Relation fields count too — rstore routes their payload writes through the same
 * bookkeeping.
 *
 * @param formBlockContext - Injected form block context (e.g. from injectFormBlockContext).
 * @param name - Field name as it appears in the form state.
 */
export function useFormFieldChanges<T = unknown>(
	formBlockContext: FormBlockContext | null,
	name: MaybeRefOrGetter<string | undefined>,
): UseFormFieldChangesReturn<T> {
	const change = computed<[unknown, unknown] | undefined>(() => {
		const fieldName = toValue(name);
		if (!fieldName) return;
		return formBlockContext?.state.value?.$changedProps?.[fieldName];
	});

	return {
		hasChanges: computed(() => !!change.value),
		originalValue: computed(() => change.value?.[1] as T | undefined),
	};
}
